import { Box, Divider, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import type { PbjRoleSummaryRow } from "../../../utils/models";

/** One label/value line of the period splits (weekend / weekday / agency / DST). */
export type BreakdownSplit = { label: string; value: string };

type Props = {
	jobTotals: PbjRoleSummaryRow[];
	/** Shared with the summary strip so both surfaces show identically-formatted hours. */
	formatHours: (hours: number) => string;
	/** Period splits to show above the per-job list — passed when the strip is too narrow to show them inline. */
	splits?: BreakdownSplit[];
};

/** Width of the code-chip column; the Unmapped marker centers in the same column so names stay aligned. */
const CODE_COLUMN_WIDTH_PX = 34;

/**
 * One breakdown line: [pay-title-code chip | warning dot] · job name · dotted leader · hours.
 * `code === null` marks the backend's trailing "Unmapped" bucket (shifts without a mapped PBJ job).
 */
const BreakdownRow: FC<{ row: PbjRoleSummaryRow; formatHours: (hours: number) => string }> = ({ row, formatHours }) => {
	const isUnmapped = row.code === null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 0.5 }} data-testid={`pbj-model-staffing-summary-job-${row.role}`}>
			{isUnmapped ? (
				<Box sx={{ width: CODE_COLUMN_WIDTH_PX, display: "flex", justifyContent: "center", flexShrink: 0 }}>
					<Box component="span" sx={{ width: BADGE.DOT_SIZE, height: BADGE.DOT_SIZE, borderRadius: "50%", backgroundColor: "warning.main" }} />
				</Box>
			) : (
				<Box
					component="span"
					sx={{
						width: CODE_COLUMN_WIDTH_PX,
						flexShrink: 0,
						fontFamily: "monospace",
						fontSize: FONT_SIZE.LABEL_XS,
						lineHeight: 1.6,
						textAlign: "center",
						color: INK.MUTED,
						backgroundColor: NEUTRAL[100],
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: 1,
					}}
				>
					{row.code}
				</Box>
			)}
			<Typography
				noWrap
				title={row.role}
				sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.4, color: isUnmapped ? INK.MUTED : INK.SECONDARY, minWidth: 0, flexShrink: 1 }}
			>
				{row.role}
			</Typography>
			{/* Dotted leader tying the name to its figure; nudged down toward the text baseline. */}
			<Box aria-hidden sx={{ flex: 1, minWidth: 16, borderBottom: `1px dotted ${NEUTRAL[300]}`, alignSelf: "center", transform: "translateY(4px)" }} />
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
				{formatHours(row.hours)}
			</Typography>
		</Box>
	);
};

/**
 * Popover body for the summary's "By job" breakdown: aligned code / name / hours lines, with the
 * backend's trailing Unmapped bucket divider-separated and marked with a warning dot so unmapped
 * hours read as needs-attention rather than just another job.
 */
export const StaffingHoursJobsBreakdown: FC<Props> = ({ jobTotals, formatHours, splits }) => {
	const { t } = useTranslation();
	const mappedRows = jobTotals.filter((row) => row.code !== null);
	const unmappedRows = jobTotals.filter((row) => row.code === null);
	return (
		// 380px fits most CMS job descriptions on one line (the longest still ellipsize with a title tooltip).
		<Box sx={{ width: 380, maxWidth: "82vw" }} data-testid="pbj-model-staffing-summary-jobs-popover">
			{splits && splits.length > 0 && (
				<Box sx={{ pb: 1 }} data-testid="pbj-model-staffing-summary-splits">
					{splits.map((split) => (
						<Box key={split.label} sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 0.375 }}>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.4, color: INK.SECONDARY }}>{split.label}</Typography>
							<Box aria-hidden sx={{ flex: 1, minWidth: 16, borderBottom: `1px dotted ${NEUTRAL[300]}`, alignSelf: "center", transform: "translateY(4px)" }} />
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
								{split.value}
							</Typography>
						</Box>
					))}
				</Box>
			)}
			<Typography
				component="p"
				sx={{
					fontSize: FONT_SIZE.LABEL_XS,
					fontWeight: 600,
					letterSpacing: "0.06em",
					textTransform: "uppercase",
					color: INK.MUTED,
					px: 0.5,
					pb: 0.75,
				}}
			>
				{t("pbj_model.staffing.summary.by_job")}
			</Typography>
			<Divider sx={{ borderColor: NEUTRAL[150] }} />
			<Box sx={{ maxHeight: 320, overflowY: "auto", pt: 0.5, pr: 0.5 }}>
				{mappedRows.map((row) => (
					<BreakdownRow key={row.role} row={row} formatHours={formatHours} />
				))}
				{unmappedRows.length > 0 && (
					<>
						{mappedRows.length > 0 && <Divider sx={{ borderColor: NEUTRAL[150], my: 0.5 }} />}
						{unmappedRows.map((row) => (
							<BreakdownRow key={row.role} row={row} formatHours={formatHours} />
						))}
					</>
				)}
			</Box>
		</Box>
	);
};
